import { useCallback } from 'react';

import type {
  PaginatedPassengerAddressListRead,
  PayerOrganizationRead,
} from 'src/common/external/bambi-api/bambiApi';

import addDefaultTripAddressOption from './addDefaultTripAddressOption';
import getRecentAddressesAsOptions from './getRecentAddressesAsOptions';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

type AdditionalResultsQueryFn = (
  query: string,
  options: SelectOption[]
) => Promise<SelectOption[]>;

export const useAdditionalResultsQuery = (
  recentAddressResponse: PaginatedPassengerAddressListRead | undefined,
  selectedPayer: PayerOrganizationRead | null | undefined
): AdditionalResultsQueryFn => {
  const additionalResultsQueryFn = useCallback(
    async (query: string, options: SelectOption[]) => {
      const additionalOptions = getAdditionalOptions(
        recentAddressResponse,
        selectedPayer,
        query
      );

      // Filter out options that are already in the recent addresses
      const filteredOptions = options.filter((option) => {
        const recentAddress = recentAddressResponse?.results.find(
          (result) => result.address.location?.place_id === option.value
        );
        return !recentAddress;
      });

      return Promise.resolve([...additionalOptions, ...filteredOptions]);
    },
    [recentAddressResponse, selectedPayer]
  );

  return additionalResultsQueryFn;
};

const getAdditionalOptions = (
  recentAddressResponse: PaginatedPassengerAddressListRead | undefined,
  selectedPayer: PayerOrganizationRead | null | undefined,
  query: string
) => {
  let additionalOptions: SelectOption[] = getRecentAddressesAsOptions(
    recentAddressResponse
  );

  additionalOptions = addDefaultTripAddressOption(
    additionalOptions,
    selectedPayer
  );

  const filteredAdditionalOptions = additionalOptions.filter((option) => {
    return option.label.toLowerCase().includes(query.toLowerCase());
  });

  return filteredAdditionalOptions;
};
